'use client';

import React, { useState, useEffect } from 'react';
import type { CompletedQuestObservation } from './DailyQuestGenerator';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface Practitioner {
  id: string;
  handle: string;
  city: string;
  ward: string;
  activeProject: string;
  yaqeenLevel: 'Ilm' | 'Ayn' | 'Haqq';
  recentObservation: string;
}

interface VocationalRole {
  id: string;
  title: string;
  arabic: string;
  color: string;
  icon: string;
  chainNext: string;
  chainPrev: string | null;
  irl_lens: string;
  amanah_domain: string;
  keys_path: string;
  mrg_bridge: string;
  fisabilillah_prompt: string;
  chainImpact: string;
  practitioners: Practitioner[];
  observations: RoleObservation[];
  mrgUpdates: MRGUpdate[];
}

interface RoleObservation {
  id: string;
  author: string;
  city: string;
  text: string;
  namesExtracted: string[];
  timestamp: string;
  yaqeen: 'Ilm' | 'Ayn' | 'Haqq';
  fromQuest?: boolean;
  questTitle?: string;
}

interface MRGUpdate {
  id: string;
  title: string;
  coAuthors: string[];
  vocation: string;
  status: 'draft' | 'review' | 'published';
  description: string;
}

// ─── DATA ─────────────────────────────────────────────────────────────────────

const VOCATIONAL_CHAIN: VocationalRole[] = [
  {
    id: 'engineer',
    title: 'Engineer',
    arabic: 'مُهَنْدِس',
    color: '#2d8a87',
    icon: '⚙',
    chainNext: 'teacher',
    chainPrev: null,
    irl_lens: 'Systems decomposition — what are the measured inputs, outputs, and feedback loops in this system?',
    amanah_domain: 'Infrastructure as trust — every system built is an Amanah to the community it serves',
    keys_path: 'Key 03 (Sunnat Allah) → Key 07 (Mizan) → Key 09 (Amanah)',
    mrg_bridge: 'Infrastructure gaps, water/energy access, transit deserts in the ward',
    fisabilillah_prompt: 'What tools are you building, and what tools do you utilize toward that purpose? If your engineering were oriented toward the remembrance of Allah, what would you measure differently?',
    chainImpact: 'The engineer\'s infrastructure analysis and systems documentation feeds the teacher\'s curriculum — the measurements become lesson material, the infrastructure gaps become case studies for the next generation.',
    practitioners: [
      { id: 'e1', handle: 'Khalil.Detroit', city: 'Detroit', ward: 'Eastside', activeProject: 'Greywater filtration system for community garden', yaqeenLevel: 'Ayn', recentObservation: 'Mapped 3 infrastructure gaps in ward — water pressure variance as Taqdir' },
      { id: 'e2', handle: 'Amina.Chicago', city: 'Chicago', ward: 'Englewood', activeProject: 'Solar micro-grid for masjid', yaqeenLevel: 'Ilm', recentObservation: 'Al-Qadir in load distribution — every watt measured and decreed' },
    ],
    observations: [
      { id: 'eo1', author: 'Khalil.Detroit', city: 'Detroit', text: 'Observed that the water pressure variance in our ward follows a pattern — lower pressure in food desert zones. The Taqdir of infrastructure reflects the Mizan transgressed. Al-Qadir in the measurement, Al-Adl in the imbalance.', namesExtracted: ['Al-Qadir', 'Al-Adl', 'Al-Musawwir'], timestamp: '2 days ago', yaqeen: 'Ayn' },
      { id: 'eo2', author: 'Amina.Chicago', city: 'Chicago', text: 'Solar panel efficiency varies by roof angle — each degree of tilt is a Taqdir. The sun\'s arc is Sunnat Allah. Engineering is reading what was already written by the Pen.', namesExtracted: ['An-Nur', 'Al-Qadir', 'Al-Khabir'], timestamp: '5 days ago', yaqeen: 'Ilm' },
    ],
    mrgUpdates: [
      { id: 'em1', title: 'Infrastructure Access Map — Detroit Eastside', coAuthors: ['Khalil.Detroit', 'Amina.Chicago'], vocation: 'Engineer', status: 'review', description: 'Co-authored ward infrastructure gap analysis with Taqdir framing — maps water, energy, and transit access as Mizan indicators' },
    ],
  },
  {
    id: 'teacher',
    title: 'Teacher',
    arabic: 'مُعَلِّم',
    color: '#c9a84c',
    icon: '✎',
    chainNext: 'scientist',
    chainPrev: 'engineer',
    irl_lens: 'Curriculum as Ayat — what does the knowledge you transmit point toward? What Names are active in your pedagogy?',
    amanah_domain: 'Knowledge as trust — "He taught Adam the names — all of them." Teaching is the continuation of this trust.',
    keys_path: 'Key 01 (Ayat) → Key 04 (Ilm) → Key 08 (Khalifah)',
    mrg_bridge: 'Educational resources, tutoring networks, curriculum development for community schools',
    fisabilillah_prompt: 'What does your curriculum look like? If your teaching were oriented toward the remembrance of Allah, what would you teach differently? What Names are you unknowingly transmitting?',
    chainImpact: 'The teacher\'s students become the scientists. The curriculum shaped by the engineer\'s infrastructure analysis produces the next generation of researchers who study the ward\'s ecological and social conditions.',
    practitioners: [
      { id: 't1', handle: 'Fatima.Atlanta', city: 'Atlanta', ward: 'West End', activeProject: 'IRL Lens curriculum for 7th grade science', yaqeenLevel: 'Haqq', recentObservation: 'Students identified Al-Muhyi in decomposition lab — unprompted' },
      { id: 't2', handle: 'Ibrahim.Newark', city: 'Newark', ward: 'Central', activeProject: 'Quran-ecology integration unit', yaqeenLevel: 'Ayn', recentObservation: 'Rain hadith (Abu Musa) as curriculum framework — three soil types as learning profiles' },
    ],
    observations: [
      { id: 'to1', author: 'Fatima.Atlanta', city: 'Atlanta', text: 'The rain hadith is the most precise curriculum framework I\'ve encountered. Three soil types: absorbs and grows (deep learner), holds and shares (community learner), neither absorbs nor grows (disengaged). The teacher\'s work is to identify which soil and respond accordingly. Al-Alim knows which soil each student is.', namesExtracted: ['Al-Alim', 'Ar-Razzaq', 'Al-Muhyi'], timestamp: '1 day ago', yaqeen: 'Haqq' },
      { id: 'to2', author: 'Ibrahim.Newark', city: 'Newark', text: 'Teaching Quran 2:31 — "He taught Adam the names, all of them" — and watching students realize they are exercising Adamic capacity when they name things. The naming is the first act. The fission is the second.', namesExtracted: ['Al-Alim', 'Al-Hakim', 'Al-Khaliq'], timestamp: '3 days ago', yaqeen: 'Ayn' },
    ],
    mrgUpdates: [
      { id: 'tm1', title: 'Community School Resource Network — Atlanta West End', coAuthors: ['Fatima.Atlanta', 'Ibrahim.Newark'], vocation: 'Teacher', status: 'published', description: 'Curriculum resources for IRL Lens integration in K-12 settings — rain hadith as pedagogical framework' },
    ],
  },
  {
    id: 'scientist',
    title: 'Scientist',
    arabic: 'عَالِم',
    color: '#8a8ab8',
    icon: '◎',
    chainNext: 'farmer',
    chainPrev: 'teacher',
    irl_lens: 'Research as fission — what Names are operative in the phenomenon you study? What Sunnat Allah sequence does your research reveal?',
    amanah_domain: 'Discovery as recognition — "You recognized what the Pen wrote." Research uncovers what was already decreed.',
    keys_path: 'Key 02 (Tafakkur) → Key 05 (Sunnat Allah) → Key 10 (Haqq)',
    mrg_bridge: 'Environmental research, community health data, ecological monitoring for ward-level Mizan assessment',
    fisabilillah_prompt: 'What breakthrough research motivates you? If your research were oriented toward the remembrance of Allah, what would you be looking for? What Names are you unknowingly serving in your methodology?',
    chainImpact: 'The scientist\'s research informs the farmer\'s planting decisions. Soil microbiome studies, air quality data, and ecological monitoring translate directly into what the farmer plants, where, and why.',
    practitioners: [
      { id: 's1', handle: 'Mariam.Houston', city: 'Houston', ward: 'Third Ward', activeProject: 'Soil microbiome study in urban community gardens', yaqeenLevel: 'Haqq', recentObservation: 'Al-Muhyi in mycorrhizal networks — decomposition as Sunnat Allah confirmed in lab' },
      { id: 's2', handle: 'Yusuf.Philadelphia', city: 'Philadelphia', ward: 'Germantown', activeProject: 'Air quality correlation with food desert boundaries', yaqeenLevel: 'Ayn', recentObservation: 'Mizan transgression measurable — PM2.5 inversely correlated with green space access' },
    ],
    observations: [
      { id: 'so1', author: 'Mariam.Houston', city: 'Houston', text: 'Mycorrhizal networks are the most precise manifestation of Al-Wali (The Protecting Friend) I\'ve encountered in research. The fungal threads protect plant roots, share nutrients, communicate stress signals. A community of care written into the soil before any of us arrived. The Pen wrote this.', namesExtracted: ['Al-Wali', 'Al-Muhyi', 'Al-Latif'], timestamp: '4 days ago', yaqeen: 'Haqq' },
      { id: 'so2', author: 'Yusuf.Philadelphia', city: 'Philadelphia', text: 'Data confirms: air quality degrades at food desert boundaries. The Mizan is measurable. Al-Adl is not abstract — it shows up in particulate matter counts. Research is reading the Qadr of injustice.', namesExtracted: ['Al-Adl', 'Al-Qadir', 'Al-Khabir'], timestamp: '6 days ago', yaqeen: 'Ayn' },
    ],
    mrgUpdates: [
      { id: 'sm1', title: 'Urban Soil Health Index — Houston Third Ward', coAuthors: ['Mariam.Houston', 'Yusuf.Philadelphia'], vocation: 'Scientist', status: 'draft', description: 'Community soil health data with Names-extraction annotations — mycorrhizal network as Sunnat Allah evidence' },
    ],
  },
  {
    id: 'farmer',
    title: 'Farmer / Gardener',
    arabic: 'فَلَّاح',
    color: '#2d7a47',
    icon: '⬡',
    chainNext: 'returning-citizen',
    chainPrev: 'scientist',
    irl_lens: 'Planting as Tafakkur — which plants, where, why? What Ayat is the gardener pondering while planting? What Sunnat Allah governs the soil?',
    amanah_domain: 'The earth as Amanah — "And not cause corruption in the earth after its reformation." (Quran 7:56) Every seed planted is a trust.',
    keys_path: 'Key 06 (Fitra) → Key 07 (Mizan) → Key 09 (Amanah)',
    mrg_bridge: 'Community gardens, food access, seed libraries, urban agriculture networks, seasonal food distribution',
    fisabilillah_prompt: 'Which kinds of plants are you planting? Where, how, why? What Ayat, Signs are you pondering while doing so? If your farming were oriented toward the remembrance of Allah, what would you plant differently?',
    chainImpact: 'The farmer\'s soil health data and food access work directly supports the returning citizen\'s reform proposals. Food desert mapping, community garden networks, and soil remediation data become evidence for institutional reform.',
    practitioners: [
      { id: 'f1', handle: 'Aisha.Baltimore', city: 'Baltimore', ward: 'Sandtown', activeProject: 'Native plant restoration in vacant lot — 12 species', yaqeenLevel: 'Haqq', recentObservation: 'Quran 7:58 confirmed in soil — pure land yields by permission of its Lord' },
      { id: 'f2', handle: 'Hassan.Minneapolis', city: 'Minneapolis', ward: 'North Side', activeProject: 'Three Sisters polyculture with Somali community', yaqeenLevel: 'Ayn', recentObservation: 'Corn-bean-squash interdependence as Sunnat Allah — Al-Latif in the subtle cooperation' },
    ],
    observations: [
      { id: 'fo1', author: 'Aisha.Baltimore', city: 'Baltimore', text: 'Planted 12 native species in the vacant lot on Fulton Ave. The soil was compacted, contaminated, abandoned. Three months later: 7 species established, 2 pollinator species returned, soil pH shifting toward neutral. "The pure land comes forth its vegetation by permission of its Lord." (Quran 7:58) — I watched this happen.', namesExtracted: ['Al-Muhyi', 'Al-Qadir', 'Ar-Razzaq', 'Al-Latif'], timestamp: '1 day ago', yaqeen: 'Haqq' },
      { id: 'fo2', author: 'Hassan.Minneapolis', city: 'Minneapolis', text: 'Three Sisters planting with the Somali aunties. Corn provides structure, beans fix nitrogen, squash shades the soil. None of them could do this alone. Al-Latif in the subtle cooperation — the plants know their roles. We are learning ours.', namesExtracted: ['Al-Latif', 'Al-Wali', 'Ar-Razzaq'], timestamp: '3 days ago', yaqeen: 'Ayn' },
    ],
    mrgUpdates: [
      { id: 'fm1', title: 'Native Plant Restoration Guide — Baltimore Sandtown', coAuthors: ['Aisha.Baltimore', 'Hassan.Minneapolis'], vocation: 'Farmer', status: 'published', description: 'Step-by-step restoration guide with Ayat annotations — Quran 7:58 as the guiding principle for soil remediation' },
    ],
  },
  {
    id: 'returning-citizen',
    title: 'Returning Citizen',
    arabic: 'عَائِد',
    color: '#a85c2d',
    icon: '◈',
    chainNext: 'engineer',
    chainPrev: 'farmer',
    irl_lens: 'Mizan restoration — where is the balance broken in this community? What reforms does someone who has experienced the system\'s failures propose for its institutions?',
    amanah_domain: 'Islah as vocation — "Whosoever of you sees an evil, let him change it." The returning citizen sees what others overlook.',
    keys_path: 'Key 07 (Mizan) → Key 08 (Khalifah) → Key 09 (Amanah)',
    mrg_bridge: 'Reentry resources, housing access, employment networks, legal aid, community institution reform proposals',
    fisabilillah_prompt: 'What reforms and resource redistribution propositions do you have for existing community institutions? What are the core tenets of your reasoning? If your advocacy were oriented toward the remembrance of Allah, what would Islah look like in your ward?',
    chainImpact: 'The returning citizen\'s reform proposals feed back to the engineer — the Islah proposals identify which infrastructure needs to be rebuilt, which systems need redesign, closing the chain and beginning the cycle again.',
    practitioners: [
      { id: 'r1', handle: 'Tariq.Chicago', city: 'Chicago', ward: 'Roseland', activeProject: 'Community institution reform proposal — reentry housing cooperative', yaqeenLevel: 'Ayn', recentObservation: 'Mapped 7 Mizan transgressions in reentry system — each one a measurable Taqdir of injustice' },
      { id: 'r2', handle: 'Bilal.Newark', city: 'Newark', ward: 'South Ward', activeProject: 'Mutual aid network for formerly incarcerated', yaqeenLevel: 'Ilm', recentObservation: 'Al-Adl in redistribution — what was taken must be restored' },
    ],
    observations: [
      { id: 'ro1', author: 'Tariq.Chicago', city: 'Chicago', text: 'Seven barriers to reentry in Roseland: housing, employment, ID, banking, transportation, healthcare, community connection. Each one a Mizan transgression. Each one addressable. The bootstrapped OS that failed us can be patched — but the complete fix is Islah from the root. Al-Adl demands it.', namesExtracted: ['Al-Adl', 'Al-Qadir', 'Al-Wali'], timestamp: '2 days ago', yaqeen: 'Ayn' },
      { id: 'ro2', author: 'Bilal.Newark', city: 'Newark', text: 'Mutual aid is Sunnat Allah in community form. The mycorrhizal network in the soil does what we\'re trying to do — share resources, communicate need, protect the vulnerable. Al-Wali in the network. We are learning from the earth.', namesExtracted: ['Al-Wali', 'Ar-Razzaq', 'Al-Latif'], timestamp: '4 days ago', yaqeen: 'Ilm' },
    ],
    mrgUpdates: [
      { id: 'rm1', title: 'Reentry Mizan Map — Chicago Roseland', coAuthors: ['Tariq.Chicago', 'Bilal.Newark'], vocation: 'Returning Citizen', status: 'review', description: 'Seven-barrier Mizan analysis with Islah reform proposals — Al-Adl as the guiding principle for institutional reform' },
    ],
  },
];

// ─── CHAIN IMPACT FLOW ────────────────────────────────────────────────────────

function ChainImpactFlow({ activeRole }: { activeRole: string }) {
  const chain = ['engineer', 'teacher', 'scientist', 'farmer', 'returning-citizen'];
  const activeIdx = chain.indexOf(activeRole);

  return (
    <div className="rounded-xl p-5 mb-6" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <p className="font-mono text-[9px] uppercase tracking-widest mb-4" style={{ color: 'rgba(212,197,169,0.3)' }}>
        Chain Impact Flow — How Each Role Feeds the Next
      </p>
      <div className="space-y-2">
        {chain.map((id, i) => {
          const role = VOCATIONAL_CHAIN.find(r => r.id === id)!;
          const nextRole = i < chain.length - 1 ? VOCATIONAL_CHAIN.find(r => r.id === chain[i + 1]) : VOCATIONAL_CHAIN.find(r => r.id === chain[0]);
          const isActive = id === activeRole;
          const isPrev = i < activeIdx;
          const isNext = i === activeIdx + 1 || (activeIdx === chain.length - 1 && i === 0);

          return (
            <div key={id}>
              <div
                className="rounded-lg p-3 transition-all"
                style={{
                  background: isActive ? `${role.color}12` : isPrev ? `${role.color}06` : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${isActive ? role.color + '40' : isPrev ? role.color + '15' : 'rgba(255,255,255,0.04)'}`,
                }}
              >
                <div className="flex items-start gap-3">
                  <span style={{ color: isActive ? role.color : isPrev ? `${role.color}60` : 'rgba(212,197,169,0.2)', fontSize: 14, flexShrink: 0, marginTop: 1 }}>{role.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-[10px] font-black uppercase tracking-widest" style={{ color: isActive ? '#F5F0E8' : isPrev ? 'rgba(212,197,169,0.5)' : 'rgba(212,197,169,0.25)' }}>
                        {role.title}
                      </span>
                      {isActive && (
                        <span className="font-mono text-[7px] px-1.5 py-0.5 rounded" style={{ background: `${role.color}20`, color: role.color, border: `1px solid ${role.color}30` }}>Active</span>
                      )}
                    </div>
                    {isActive && (
                      <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.6 }}>{role.chainImpact}</p>
                    )}
                  </div>
                </div>
              </div>
              {i < chain.length - 1 && (
                <div className="flex items-center gap-2 py-1 pl-5">
                  <div className="w-px h-4" style={{ background: isActive || isPrev ? `${role.color}30` : 'rgba(255,255,255,0.04)' }} />
                  <span className="font-mono text-[8px]" style={{ color: isActive ? `${role.color}60` : 'rgba(212,197,169,0.15)' }}>↓ feeds into</span>
                </div>
              )}
            </div>
          );
        })}
        {/* Loop back */}
        <div className="flex items-center gap-2 pl-5">
          <span className="font-mono text-[8px]" style={{ color: 'rgba(212,197,169,0.2)' }}>↺ cycle continues — Returning Citizen reform proposals feed back to Engineer</span>
        </div>
      </div>
    </div>
  );
}

// ─── CHAIN VISUALIZATION ──────────────────────────────────────────────────────

function VocationalChainViz({ activeRole, onSelect }: { activeRole: string; onSelect: (id: string) => void }) {
  const chain = ['engineer', 'teacher', 'scientist', 'farmer', 'returning-citizen'];
  return (
    <div className="flex items-center gap-1 flex-wrap mb-8">
      {chain.map((id, i) => {
        const role = VOCATIONAL_CHAIN.find(r => r.id === id)!;
        return (
          <React.Fragment key={id}>
            <button
              onClick={() => onSelect(id)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all font-mono text-[10px] uppercase tracking-widest"
              style={{
                background: activeRole === id ? `${role.color}18` : 'rgba(255,255,255,0.03)',
                border: `1px solid ${activeRole === id ? role.color + '60' : 'rgba(255,255,255,0.06)'}`,
                color: activeRole === id ? role.color : 'rgba(212,197,169,0.4)',
              }}
            >
              <span>{role.icon}</span>
              <span>{role.title}</span>
            </button>
            {i < chain.length - 1 && (
              <span style={{ color: 'rgba(212,197,169,0.2)', fontSize: 10 }}>→</span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── PRACTITIONER CARD ────────────────────────────────────────────────────────

function PractitionerCard({ p, color }: { p: Practitioner; color: string }) {
  const yaqeenColors: Record<string, string> = { Ilm: '#8a8ab8', Ayn: '#c9a84c', Haqq: '#2d7a47' };
  return (
    <div className="rounded-xl p-4" style={{ background: `${color}06`, border: `1px solid ${color}18` }}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-mono text-xs font-black" style={{ color }}>{p.handle}</p>
          <p className="font-mono text-[9px] uppercase tracking-widest mt-0.5" style={{ color: 'rgba(212,197,169,0.4)' }}>{p.city} · {p.ward}</p>
        </div>
        <span className="font-mono text-[8px] px-2 py-0.5 rounded" style={{ background: `${yaqeenColors[p.yaqeenLevel]}18`, color: yaqeenColors[p.yaqeenLevel], border: `1px solid ${yaqeenColors[p.yaqeenLevel]}30` }}>
          {p.yaqeenLevel}
        </span>
      </div>
      <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(212,197,169,0.35)' }}>Active Project</p>
      <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 12, lineHeight: 1.6, marginBottom: 8 }}>{p.activeProject}</p>
      <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(212,197,169,0.35)' }}>Recent Observation</p>
      <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.5, fontStyle: 'italic' }}>{p.recentObservation}</p>
    </div>
  );
}

// ─── OBSERVATION CARD ─────────────────────────────────────────────────────────

function ObservationCard({ obs, color }: { obs: RoleObservation; color: string }) {
  const yaqeenColors: Record<string, string> = { Ilm: '#8a8ab8', Ayn: '#c9a84c', Haqq: '#2d7a47' };
  return (
    <div className="rounded-xl p-5" style={{ background: obs.fromQuest ? 'rgba(45,138,135,0.04)' : 'rgba(255,255,255,0.02)', border: obs.fromQuest ? '1px solid rgba(45,138,135,0.2)' : '1px solid rgba(255,255,255,0.06)' }}>
      {obs.fromQuest && (
        <div className="flex items-center gap-2 mb-3">
          <span className="font-mono text-[7px] px-2 py-0.5 rounded uppercase tracking-widest" style={{ background: 'rgba(45,138,135,0.12)', color: '#2d8a87', border: '1px solid rgba(45,138,135,0.25)' }}>
            ◎ From Quest: {obs.questTitle}
          </span>
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] font-black" style={{ color }}>{obs.author}</span>
          <span className="font-mono text-[9px]" style={{ color: 'rgba(212,197,169,0.3)' }}>{obs.city}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-mono text-[8px] px-2 py-0.5 rounded" style={{ background: `${yaqeenColors[obs.yaqeen]}12`, color: yaqeenColors[obs.yaqeen], border: `1px solid ${yaqeenColors[obs.yaqeen]}25` }}>{obs.yaqeen}</span>
          <span className="font-mono text-[8px]" style={{ color: 'rgba(212,197,169,0.25)' }}>{obs.timestamp}</span>
        </div>
      </div>
      <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 13, lineHeight: 1.75, marginBottom: 12 }}>{obs.text}</p>
      <div className="flex flex-wrap gap-1">
        {obs.namesExtracted.map(n => (
          <span key={n} className="font-mono text-[8px] px-2 py-0.5 rounded" style={{ background: 'rgba(201,168,76,0.08)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.15)' }}>{n}</span>
        ))}
      </div>
    </div>
  );
}

// ─── MRG UPDATE CARD ──────────────────────────────────────────────────────────

function MRGUpdateCard({ update, color }: { update: MRGUpdate; color: string }) {
  const statusColors: Record<string, string> = { draft: '#8a8ab8', review: '#c9a84c', published: '#2d7a47' };
  return (
    <div className="rounded-xl p-5" style={{ background: `${color}05`, border: `1px solid ${color}20` }}>
      <div className="flex items-start justify-between mb-3">
        <p className="font-mono text-[10px] font-black uppercase tracking-widest" style={{ color }}>{update.title}</p>
        <span className="font-mono text-[8px] px-2 py-0.5 rounded capitalize" style={{ background: `${statusColors[update.status]}15`, color: statusColors[update.status], border: `1px solid ${statusColors[update.status]}30` }}>{update.status}</span>
      </div>
      <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, lineHeight: 1.6, marginBottom: 10 }}>{update.description}</p>
      <div>
        <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(212,197,169,0.3)' }}>Co-Authors</p>
        <div className="flex flex-wrap gap-1">
          {update.coAuthors.map(a => (
            <span key={a} className="font-mono text-[9px] px-2 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(212,197,169,0.5)', border: '1px solid rgba(255,255,255,0.06)' }}>{a}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── MRG CO-AUTHOR PANEL ──────────────────────────────────────────────────────

function MRGCoAuthorPanel({ role, questObservations }: { role: VocationalRole; questObservations: RoleObservation[] }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [selectedObs, setSelectedObs] = useState<string[]>([]);

  const allObs = [...role.observations, ...questObservations];

  const handleSubmit = () => {
    if (title.trim() && description.trim()) {
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-xl p-5" style={{ background: 'rgba(45,122,71,0.08)', border: '1px solid rgba(45,122,71,0.25)' }}>
        <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: 'rgba(45,122,71,0.7)' }}>✓ MRG Update Submitted for Review</p>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, lineHeight: 1.6 }}>
          Your co-authored MRG entry has been submitted. It will be reviewed by other practitioners in the {role.title} vocational lens before publication.
        </p>
        <button
          onClick={() => { setSubmitted(false); setTitle(''); setDescription(''); setSelectedObs([]); }}
          className="mt-3 font-mono text-[9px] uppercase tracking-widest px-4 py-2 rounded"
          style={{ background: 'rgba(45,122,71,0.1)', color: 'rgba(45,122,71,0.7)', border: '1px solid rgba(45,122,71,0.2)' }}
        >
          Submit Another
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl p-5" style={{ background: 'rgba(45,122,71,0.04)', border: '1px solid rgba(45,122,71,0.15)', borderStyle: 'dashed' }}>
        <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(45,122,71,0.6)' }}>
          Co-Author an MRG Entry — {role.title} Vocation
        </p>
        <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 12, lineHeight: 1.6, marginBottom: 16 }}>
          Your role-specific knowledge shapes the resource guide for your community. Quest observations can be woven directly into MRG entries.
        </p>

        {/* Select observations to include */}
        {allObs.length > 0 && (
          <div className="mb-4">
            <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.35)' }}>
              Include Observations ({selectedObs.length} selected)
            </p>
            <div className="space-y-2">
              {allObs.slice(0, 4).map(obs => (
                <button
                  key={obs.id}
                  onClick={() => setSelectedObs(prev => prev.includes(obs.id) ? prev.filter(id => id !== obs.id) : [...prev, obs.id])}
                  className="w-full text-left rounded-lg p-3 transition-all"
                  style={{
                    background: selectedObs.includes(obs.id) ? 'rgba(45,122,71,0.1)' : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${selectedObs.includes(obs.id) ? 'rgba(45,122,71,0.3)' : 'rgba(255,255,255,0.06)'}`,
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span style={{ color: selectedObs.includes(obs.id) ? 'rgba(45,122,71,0.8)' : 'rgba(212,197,169,0.2)', fontSize: 10 }}>
                      {selectedObs.includes(obs.id) ? '✓' : '◦'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>{obs.author} · {obs.yaqeen}</p>
                      <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.5 }}>{obs.text.slice(0, 80)}...</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <p className="font-mono text-[8px] uppercase tracking-widest mb-1.5" style={{ color: 'rgba(212,197,169,0.35)' }}>MRG Entry Title</p>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={`${role.mrg_bridge.split(',')[0]} — [Your Ward]`}
              className="w-full rounded-lg px-3 py-2 font-mono text-[11px] focus:outline-none"
              style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,122,71,0.2)', color: 'rgba(212,197,169,0.75)' }}
            />
          </div>
          <div>
            <p className="font-mono text-[8px] uppercase tracking-widest mb-1.5" style={{ color: 'rgba(212,197,169,0.35)' }}>Description — What does this resource provide?</p>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={4}
              placeholder={`${role.amanah_domain}...`}
              className="w-full rounded-lg px-3 py-2 font-mono text-[11px] resize-none focus:outline-none"
              style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(45,122,71,0.2)', color: 'rgba(212,197,169,0.75)', lineHeight: 1.6 }}
            />
          </div>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || !description.trim()}
            className="font-mono text-[9px] uppercase tracking-widest px-4 py-2.5 rounded-lg transition-all"
            style={{
              background: title.trim() && description.trim() ? 'rgba(45,122,71,0.15)' : 'rgba(255,255,255,0.03)',
              color: title.trim() && description.trim() ? 'rgba(45,122,71,0.8)' : 'rgba(212,197,169,0.2)',
              border: `1px solid ${title.trim() && description.trim() ? 'rgba(45,122,71,0.3)' : 'rgba(255,255,255,0.06)'}`,
              cursor: title.trim() && description.trim() ? 'pointer' : 'not-allowed',
            }}
          >
            Submit MRG Entry for Review →
          </button>
        </div>
      </div>

      {/* Existing MRG updates */}
      {role.mrgUpdates.map(u => (
        <MRGUpdateCard key={u.id} update={u} color={role.color} />
      ))}
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function VocationalNetwork() {
  const [activeRoleId, setActiveRoleId] = useState('engineer');
  const [activeTab, setActiveTab] = useState<'overview' | 'chain-impact' | 'practitioners' | 'observations' | 'mrg'>('overview');
  const [questObservations, setQuestObservations] = useState<RoleObservation[]>([]);

  // Load completed quest observations from localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const stored: CompletedQuestObservation[] = JSON.parse(
        localStorage.getItem('haal_completed_quest_observations') || '[]'
      );
      // Convert to RoleObservation format, filtered by active role
      const converted: RoleObservation[] = stored
        .filter(obs => obs.lensId === activeRoleId && obs.observationText.trim())
        .map((obs, i) => ({
          id: `quest-obs-${i}`,
          author: 'You (Quest Completion)',
          city: 'Your Ward',
          text: obs.observationText.replace(/^Quest:.*\n.*\n.*\n.*\n.*\n\nMy Observation:\n/, '').trim() || obs.observationText,
          namesExtracted: obs.namesExtracted,
          timestamp: 'Just now',
          yaqeen: obs.yaqeenLevel,
          fromQuest: true,
          questTitle: obs.questTitle,
        }));
      setQuestObservations(converted);
    }
  }, [activeRoleId]);

  const role = VOCATIONAL_CHAIN.find(r => r.id === activeRoleId)!;
  const allObservations = [...questObservations, ...role.observations];

  return (
    <section id="section-vocational-network" style={{ background: 'rgba(6,13,18,0.98)', borderTop: '1px solid rgba(201,168,76,0.08)', padding: '80px 0' }}>
      <div className="max-w-[900px] mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="mb-10">
          <p className="font-mono text-[9px] tracking-widest uppercase mb-4" style={{ color: 'rgba(139,105,20,0.6)' }}>
            ◈ Cross-Garden Vocational Network
          </p>
          <h2 className="font-mono text-3xl md:text-4xl font-black uppercase tracking-tight mb-3" style={{ color: '#F5F0E8' }}>
            The Roles Are a System
          </h2>
          <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 14, lineHeight: 1.75, maxWidth: 640, marginBottom: 8 }}>
            "The funding structure that sustains the university hosts the classes that produce the law student who drafts the legislation that addresses the concerns that the janitor identified." Each role feeds the next. Completed quests surface as role-matched observations visible to other practitioners.
          </p>
          <p style={{ color: 'rgba(212,197,169,0.35)', fontSize: 12, lineHeight: 1.6, maxWidth: 600, fontStyle: 'italic' }}>
            Fisabilillah — in the way of Allah, you are entrusted with something that is running out, and you're entrusted with the means to do something in that period.
          </p>
        </div>

        {/* Chain Visualization */}
        <VocationalChainViz activeRole={activeRoleId} onSelect={(id) => { setActiveRoleId(id); setActiveTab('overview'); }} />

        {/* Active Role Header */}
        <div className="rounded-2xl p-6 mb-6" style={{ background: `${role.color}08`, border: `1px solid ${role.color}30` }}>
          <div className="flex items-start gap-4 mb-4">
            <span style={{ fontSize: 28, color: role.color }}>{role.icon}</span>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h3 className="font-mono text-2xl font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>{role.title}</h3>
                <span style={{ fontFamily: "'Amiri', serif", fontSize: 20, color: `${role.color}70`, direction: 'rtl' }}>{role.arabic}</span>
              </div>
              {role.chainPrev && (
                <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.3)' }}>
                  ← receives from: {VOCATIONAL_CHAIN.find(r => r.id === role.chainPrev)?.title} · feeds into: {VOCATIONAL_CHAIN.find(r => r.id === role.chainNext)?.title} →
                </p>
              )}
              {!role.chainPrev && (
                <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.3)' }}>
                  chain origin · feeds into: {VOCATIONAL_CHAIN.find(r => r.id === role.chainNext)?.title} →
                </p>
              )}
            </div>
          </div>
          {/* Fisabilillah Prompt */}
          <div className="rounded-lg p-4" style={{ background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)', borderLeft: '3px solid rgba(45,122,71,0.4)' }}>
            <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: 'rgba(45,122,71,0.6)' }}>Fisabilillah Prompt</p>
            <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 13, lineHeight: 1.7, fontStyle: 'italic' }}>{role.fisabilillah_prompt}</p>
          </div>
        </div>

        {/* Tab Selector */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: 'overview', label: 'Role Overview' },
            { id: 'chain-impact', label: 'Chain Impact' },
            { id: 'practitioners', label: `Practitioners (${role.practitioners.length})` },
            { id: 'observations', label: `Observations (${allObservations.length}${questObservations.length > 0 ? ` · ${questObservations.length} from quests` : ''})` },
            { id: 'mrg', label: `MRG Co-Author` },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id as typeof activeTab)}
              className="font-mono text-[9px] tracking-widest uppercase px-4 py-2 transition-all"
              style={{
                background: activeTab === t.id ? `${role.color}12` : 'transparent',
                border: `1px solid ${activeTab === t.id ? role.color + '50' : 'rgba(255,255,255,0.06)'}`,
                color: activeTab === t.id ? role.color : 'rgba(212,197,169,0.35)',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {[
              { label: 'IRL Lens Orientation', value: role.irl_lens, icon: '◎', color: '#2d8a87' },
              { label: 'Amanah Domain', value: role.amanah_domain, icon: '⬡', color: '#c9a84c' },
              { label: 'Keys Path', value: role.keys_path, icon: '⌘', color: '#8a8ab8' },
              { label: 'MRG Bridge', value: role.mrg_bridge, icon: '◈', color: '#2d7a47' },
            ].map(item => (
              <div key={item.label} className="flex items-start gap-4 p-4 rounded-xl" style={{ background: `${item.color}06`, border: `1px solid ${item.color}18` }}>
                <span style={{ color: item.color, fontSize: 16, flexShrink: 0, marginTop: 2 }}>{item.icon}</span>
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: `${item.color}80` }}>{item.label}</p>
                  <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 13, lineHeight: 1.65 }}>{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'chain-impact' && (
          <ChainImpactFlow activeRole={activeRoleId} />
        )}

        {activeTab === 'practitioners' && (
          <div className="space-y-4">
            <p className="font-mono text-[9px] uppercase tracking-widest mb-4" style={{ color: 'rgba(212,197,169,0.3)' }}>
              Active practitioners in this vocational lens — viewing their projects, observations, and connections
            </p>
            {role.practitioners.map(p => (
              <PractitionerCard key={p.id} p={p} color={role.color} />
            ))}
          </div>
        )}

        {activeTab === 'observations' && (
          <div className="space-y-4">
            {questObservations.length > 0 && (
              <div className="rounded-lg p-3 mb-2" style={{ background: 'rgba(45,138,135,0.06)', border: '1px solid rgba(45,138,135,0.2)' }}>
                <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(45,138,135,0.7)' }}>
                  ◎ {questObservations.length} quest completion{questObservations.length !== 1 ? 's' : ''} surfaced as role-matched observation{questObservations.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}
            <p className="font-mono text-[9px] uppercase tracking-widest mb-4" style={{ color: 'rgba(212,197,169,0.3)' }}>
              Role-specific observations — quest completions and practitioner submissions
            </p>
            {allObservations.length === 0 ? (
              <div className="rounded-xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <p style={{ color: 'rgba(212,197,169,0.35)', fontSize: 13 }}>Complete quests in the Daily Quest Generator to surface your observations here.</p>
              </div>
            ) : (
              allObservations.map(obs => (
                <ObservationCard key={obs.id} obs={obs} color={role.color} />
              ))
            )}
          </div>
        )}

        {activeTab === 'mrg' && (
          <MRGCoAuthorPanel role={role} questObservations={questObservations} />
        )}

        {/* Chain Connection Note */}
        <div className="mt-8 rounded-xl p-5" style={{ background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.12)' }}>
          <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: 'rgba(201,168,76,0.5)' }}>Vocational Chain Connection</p>
          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, lineHeight: 1.65 }}>
            The engineer's infrastructure analysis feeds the teacher's curriculum. The teacher's students become the scientists. The scientist's research informs the farmer's planting decisions. The farmer's soil health data supports the returning citizen's reform proposals. The chain is a system — not a list. Each role's work is Mata for the next.
          </p>
        </div>
      </div>
    </section>
  );
}
